// Admin-side case editor for Coravu-Managed (RM) cases that were created
// on behalf of a client via the admin_create_rm_case RPC. Mirrors the
// client editor's shape but exposes admin-only scope: engagement_model
// is editable, tier picker is inline, and the submit action calls
// submit_case_for_review (which now accepts admin callers).
//
// Access: super_admin + admin_rm only. admin_licensing is bounced.
// Reads + writes use the shared lib helpers; admin RLS lets them
// see/write any competition row, so we route through the same
// updateCompetition path the client editor uses.

import type { Href } from "expo-router";
import { router, useFocusEffect, useLocalSearchParams } from "expo-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { Platform, Pressable, ScrollView, Text, View, type ViewStyle } from "react-native";

import { useAuth } from "../../../../../lib/authContext";
import {
  formatCents,
  getCaseForReview,
  listPricingTiers,
  submitForReview,
  type CaseForReview,
} from "../../../../../lib/caseApprovals";
import { updateCompetition } from "../../../../../lib/clientCompetitions";
import { logAdminAction } from "../../../../../lib/auditLog";
import type {
  CompetitionTier,
  EngagementModel,
  PricingTier,
} from "../../../../../types/database";
import { RADII, SPACING, STROKES } from "../../../../../lib/brand";
import { toIsoOrThrow } from "../../../../../lib/datetime";
import { PANE_THEMES } from "../../../../../components/brand/paneThemes";
import {
  AdminBanner,
  AdminButton,
  AdminError,
  AdminInput,
  AdminLoading,
  AdminPageHeader,
  FilterChip,
} from "../../../../../components/admin/ui";
import { SectionHeader } from "../../../../../components/aspects/SectionHeader";
import { StatusPill, type StatusPillTone } from "../../../../../components/aspects/StatusPill";

const THEME = PANE_THEMES.Admin;

const ENGAGEMENTS: EngagementModel[] = ["licensing", "rm", "hybrid"];

function statusTone(status: string): StatusPillTone {
  if (status === "draft") return "amber";
  if (status === "submitted" || status === "under_review") return "sage";
  return "grey";
}

type FormState = {
  name: string;
  brief: string;
  deliverable_description: string;
  engagement_model: EngagementModel;
  tier: CompetitionTier | null;
  scholarshipDollars: string;
  opens_at: string;
  closes_at: string;
  team_size_min: string;
  max_team_size: string;
};

function caseToForm(c: CaseForReview): FormState {
  // Scholarship pre-fills from the sum of prize_structure.amount_cents,
  // matching the approvals quote builder.
  const prizeCents = Array.isArray(c.prize_structure)
    ? (c.prize_structure as { amount_cents?: number }[]).reduce(
        (s, p) => s + (typeof p?.amount_cents === "number" ? p.amount_cents : 0),
        0,
      )
    : 0;
  return {
    name: c.name ?? "",
    brief: c.brief ?? "",
    deliverable_description: c.deliverable_description ?? "",
    engagement_model: (c.engagement_model as EngagementModel) ?? "rm",
    tier: (c.tier as CompetitionTier | null) ?? null,
    scholarshipDollars: prizeCents > 0 ? String(prizeCents / 100) : "",
    opens_at: c.opens_at ? String(c.opens_at).slice(0, 16) : "",
    closes_at: c.closes_at ? String(c.closes_at).slice(0, 16) : "",
    team_size_min: String(c.team_size_min ?? 1),
    max_team_size: String(c.max_team_size ?? 4),
  };
}

export default function AdminCaseEditScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const { role } = useAuth();
  const canAccess = role === "super_admin" || role === "admin_rm";

  const [detail, setDetail] = useState<CaseForReview | null>(null);
  const [pricing, setPricing] = useState<PricingTier[]>([]);
  const [form, setForm] = useState<FormState | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [savedToast, setSavedToast] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const hasLoadedRef = useRef(false);

  const load = useCallback(async () => {
    if (typeof id !== "string") return;
    if (!hasLoadedRef.current) setLoading(true);
    setErr(null);
    try {
      const [d, p] = await Promise.all([
        getCaseForReview(id),
        pricing.length > 0 ? Promise.resolve(pricing) : listPricingTiers(),
      ]);
      setDetail(d);
      if (d) setForm(caseToForm(d));
      if (pricing.length === 0) setPricing(p);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to load case");
    } finally {
      hasLoadedRef.current = true;
      setLoading(false);
    }
    // pricing.length stable after first fetch — intentional dep choice
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useFocusEffect(
    useCallback(() => {
      void load();
    }, [load]),
  );

  // Inline toast auto-dismisses after 4s — matches the rest of the admin app.
  useEffect(() => {
    if (!savedToast) return;
    const t = setTimeout(() => setSavedToast(false), 4000);
    return () => clearTimeout(t);
  }, [savedToast]);

  const status = detail?.status;
  const isDraftOrDenied = status === "draft" || status === "denied";

  // Build the payload from form state. Scholarship dollars → a single
  // prize_structure row at place 1 (admin can refine in the full client
  // editor if they want multi-place splits — keeping the admin shortcut
  // simple).
  function buildPatch(f: FormState) {
    const scholarshipCents = (() => {
      const n = parseFloat(f.scholarshipDollars);
      if (!Number.isFinite(n) || n <= 0) return 0;
      return Math.round(n * 100);
    })();

    const prize_structure =
      scholarshipCents > 0 ? [{ place: 1, amount_cents: scholarshipCents }] : [];

    return {
      name: f.name.trim() || "Untitled Coravu-Managed case",
      brief: f.brief.trim() || null,
      deliverable_description: f.deliverable_description.trim() || null,
      engagement_model: f.engagement_model,
      tier: f.tier,
      opens_at: toIsoOrThrow(f.opens_at, "Opens at"),
      closes_at: toIsoOrThrow(f.closes_at, "Closes at"),
      team_size_min: Math.max(1, parseInt(f.team_size_min, 10) || 1),
      max_team_size: Math.max(1, parseInt(f.max_team_size, 10) || 4),
      prize_structure,
    };
  }

  async function onSaveDraft() {
    if (!form || !detail || saving) return;
    setSaving(true);
    setErr(null);
    try {
      const patch = buildPatch(form);
      const { error } = await updateCompetition(detail.id, patch);
      if (error) throw error;
      setDetail({
        ...detail,
        ...patch,
        prize_structure: patch.prize_structure,
      } as CaseForReview);
      setSavedToast(true);
      void logAdminAction({
        action: "competition.admin_edit",
        entityType: "competitions",
        entityId: detail.id,
        summary: `Edited Coravu-Managed case: ${patch.name}`,
        diff: { after: { tier: patch.tier, engagement_model: patch.engagement_model } },
      });
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }

  async function onSendForReview() {
    if (!form || !detail || submitting) return;
    setSubmitting(true);
    setErr(null);
    try {
      const patch = buildPatch(form);
      const { error: updErr } = await updateCompetition(detail.id, patch);
      if (updErr) throw updErr;
      await submitForReview(detail.id);
      void logAdminAction({
        action: "competition.admin_submitted_on_behalf",
        entityType: "competitions",
        entityId: detail.id,
        summary: `Submitted Coravu-Managed case for review on behalf of ${detail.client_full_name ?? "client"}`,
        diff: { after: { status: "submitted" } },
      });
      router.replace(`/admin/case-approvals?case=${detail.id}` as Href);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Submit failed");
      setSubmitting(false);
    }
  }

  if (!canAccess) {
    return (
      <ScrollView style={{ flex: 1, backgroundColor: THEME.pageBg }}>
        <View style={{ padding: SPACING.lg, gap: 12 }}>
          <AdminBanner
            theme={THEME}
            tone="error"
            title="Access denied"
            message="Coravu-Managed cases can only be edited by super_admin or admin_rm."
          />
          <AdminButton theme={THEME} label="Back to clients" variant="outline" size="sm" onPress={() => router.replace("/admin/clients" as Href)} />
        </View>
      </ScrollView>
    );
  }

  if (loading || !form || !detail) {
    return (
      <View style={{ flex: 1, justifyContent: "center", backgroundColor: THEME.pageBg }}>
        <AdminLoading theme={THEME} label="Loading case…" />
      </View>
    );
  }

  return (
    <ScrollView style={{ flex: 1, backgroundColor: THEME.pageBg }} contentContainerStyle={{ paddingBottom: 80 }}>
      <View style={{ padding: SPACING.lg, maxWidth: 880 }}>
        <BackLink onFallback={() => router.replace("/admin/clients" as Href)} />

        <AdminPageHeader
          theme={THEME}
          eyebrow="Coravu-Managed case"
          title={`Editing: ${form.name || "Untitled Coravu-Managed case"}`}
          subtitle={`Coravu-Managed case for ${detail.client_full_name ?? "—"}${detail.company_name ? ` (${detail.company_name})` : ""}`}
        />
        <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 6, alignItems: "center", marginTop: -SPACING.md, marginBottom: SPACING.md }}>
          <StatusPill theme={THEME} label={detail.status} tone={statusTone(detail.status)} />
          {detail.tier ? <StatusPill theme={THEME} label={`Tier ${detail.tier}`} tone="sage" showDot={false} /> : null}
        </View>

        {err ? <View style={{ marginBottom: SPACING.md }}><AdminError theme={THEME} message={err} onRetry={() => void load()} /></View> : null}
        {savedToast ? <View style={{ marginBottom: SPACING.md }}><AdminBanner theme={THEME} tone="success" message="Draft saved." /></View> : null}

        {/* Basics */}
        <SectionHeader theme={THEME} title="Basics" tone="dark" />
        <View style={{ gap: 10 }}>
          <AdminInput theme={THEME} label="Case name" value={form.name} onChangeText={(v) => setForm({ ...form, name: v })} />
          <AdminInput
            theme={THEME}
            label="Brief"
            value={form.brief}
            onChangeText={(v) => setForm({ ...form, brief: v })}
            multiline
            minHeight={90}
            placeholder="One paragraph describing what students are working on."
          />
          <AdminInput
            theme={THEME}
            label="Deliverable description"
            value={form.deliverable_description}
            onChangeText={(v) => setForm({ ...form, deliverable_description: v })}
            multiline
            minHeight={90}
            placeholder="What students will produce. Detailed deliverable rows live in the full editor."
          />
        </View>

        {/* Engagement (admin can change unlike client side) */}
        <View style={{ marginTop: SPACING.lg }}>
          <SectionHeader theme={THEME} title="Engagement model" tone="dark" />
          <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
            {ENGAGEMENTS.map((e) => (
              <FilterChip
                key={e}
                theme={THEME}
                label={e === "rm" ? "Coravu RM" : e === "licensing" ? "Licensing" : "Hybrid"}
                active={form.engagement_model === e}
                onPress={() => setForm({ ...form, engagement_model: e })}
              />
            ))}
          </View>
        </View>

        {/* Tier */}
        <View style={{ marginTop: SPACING.lg }}>
          <SectionHeader theme={THEME} title="Tier" tone="dark" />
          <TierPicker
            engagement={form.engagement_model as "licensing" | "rm" | "hybrid"}
            pricing={pricing}
            selected={form.tier}
            onSelect={(t) => setForm({ ...form, tier: t })}
          />
        </View>

        {/* Scholarship */}
        <View style={{ marginTop: SPACING.lg }}>
          <SectionHeader theme={THEME} title="Scholarship pool" tone="dark" />
          <AdminInput
            theme={THEME}
            label="Scholarship ($)"
            value={form.scholarshipDollars}
            onChangeText={(v) => setForm({ ...form, scholarshipDollars: v })}
            placeholder="0.00"
            keyboardType="numeric"
            hint="Dollar amount that funds student prizes. Stored as a single-place prize_structure row."
          />
        </View>

        {/* Schedule */}
        <View style={{ marginTop: SPACING.lg }}>
          <SectionHeader theme={THEME} title="Schedule" tone="dark" />
          <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 10 }}>
            <View style={{ flexBasis: 220, flexGrow: 1 }}>
              <AdminInput theme={THEME} label="Opens at" value={form.opens_at} onChangeText={(v) => setForm({ ...form, opens_at: v })} placeholder="YYYY-MM-DDTHH:MM" autoCapitalize="none" />
            </View>
            <View style={{ flexBasis: 220, flexGrow: 1 }}>
              <AdminInput theme={THEME} label="Closes at" value={form.closes_at} onChangeText={(v) => setForm({ ...form, closes_at: v })} placeholder="YYYY-MM-DDTHH:MM" autoCapitalize="none" />
            </View>
          </View>
          <View style={{ marginTop: 10, flexDirection: "row", flexWrap: "wrap", gap: 10 }}>
            <View style={{ flexBasis: 160, flexGrow: 1 }}>
              <AdminInput theme={THEME} label="Min team size" value={form.team_size_min} onChangeText={(v) => setForm({ ...form, team_size_min: v })} keyboardType="numeric" />
            </View>
            <View style={{ flexBasis: 160, flexGrow: 1 }}>
              <AdminInput theme={THEME} label="Max team size" value={form.max_team_size} onChangeText={(v) => setForm({ ...form, max_team_size: v })} keyboardType="numeric" />
            </View>
          </View>
        </View>

        {/* Read-only meta */}
        <View style={{ marginTop: SPACING.lg }}>
          <SectionHeader theme={THEME} title="Metadata" tone="dark" />
          <MetaRow k="Client" v={detail.client_full_name ?? detail.client_email ?? "—"} />
          <MetaRow k="Client email" v={detail.client_email ?? "—"} />
          <MetaRow k="Company" v={detail.company_name ?? "—"} />
          <MetaRow k="Created by" v="Client (admin-on-behalf-of)" />
        </View>

        {/* For deliverables / judges / rubric — link to the full client editor. */}
        <View style={{ marginTop: SPACING.lg }}>
          <SectionHeader theme={THEME} title="Deliverables, judges, rubric" tone="dark" />
          <Text style={{ fontFamily: THEME.bodyFont, fontSize: 12.5, color: THEME.textOnSurfaceDim, lineHeight: 18 }}>
            The full editor (deliverables, judging criteria, rubric weights, judge invites) lives at the client editor route. Admins can access it with the same write permissions.
          </Text>
          <View style={{ marginTop: 10 }}>
            <AdminButton theme={THEME} label="Open full editor →" variant="outline" size="sm" onPress={() => router.push(`/client/competition/${detail.id}/edit` as Href)} />
          </View>
        </View>

        {/* Actions */}
        <View style={{ marginTop: 30, flexDirection: "row", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
          <AdminButton theme={THEME} label="Save draft" variant="outline" loading={saving} onPress={() => void onSaveDraft()} />
          {isDraftOrDenied ? (
            <AdminButton
              theme={THEME}
              label="Send for quote review"
              loading={submitting}
              disabled={!form.tier}
              onPress={() => void onSendForReview()}
            />
          ) : (
            <Text style={{ fontFamily: THEME.bodyFont, fontSize: 11.5, color: THEME.textOnSurfaceDim, alignSelf: "center" }}>
              Status is &quot;{detail.status}&quot; — submit-for-review is only available from draft / denied.
            </Text>
          )}
        </View>
        {isDraftOrDenied && !form.tier ? (
          <Text style={{ marginTop: 8, fontFamily: THEME.bodyFont, fontSize: 11, color: THEME.textOnSurfaceDim }}>
            Pick a tier before sending for quote review.
          </Text>
        ) : null}
      </View>
    </ScrollView>
  );
}

function BackLink({ onFallback }: { onFallback: () => void }) {
  return (
    <Pressable
      onPress={() => {
        if (router.canGoBack()) router.back();
        else onFallback();
      }}
      style={[
        { alignSelf: "flex-start", paddingVertical: 4, marginBottom: 6 } as ViewStyle,
        Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
      ]}
    >
      <Text style={{ fontFamily: THEME.bodyMediumFont, fontSize: 11.5, letterSpacing: 1.2, textTransform: "uppercase", color: THEME.textOnSurfaceDim }}>
        ← Back
      </Text>
    </Pressable>
  );
}

// ─── TierPicker (lifted from case-approvals) ─────────────────────────────

function TierPicker({
  engagement,
  pricing,
  selected,
  onSelect,
}: {
  engagement: "licensing" | "rm" | "hybrid";
  pricing: PricingTier[];
  selected: CompetitionTier | null;
  onSelect: (tier: CompetitionTier) => void;
}) {
  if (engagement === "hybrid") {
    return (
      <AdminBanner
        theme={THEME}
        tone="error"
        message="Hybrid engagement has no pricing rows. Switch to RM or Licensing before picking a tier."
      />
    );
  }
  const allTiers: CompetitionTier[] =
    engagement === "rm" ? ["100", "250", "500"] : ["100", "250", "500", "unlimited"];
  return (
    <View style={{ borderRadius: RADII.sm, borderWidth: STROKES.hairline, borderColor: THEME.border, backgroundColor: THEME.surfaceRaisedBg, overflow: "hidden" }}>
      {allTiers.map((tier, idx) => {
        const row = pricing.find((p) => p.tier === tier && p.engagement_model === engagement);
        const isSelected = selected === tier;
        return (
          <Pressable
            key={tier}
            onPress={() => onSelect(tier)}
            style={[
              {
                flexDirection: "row",
                alignItems: "center",
                gap: 10,
                paddingHorizontal: 12,
                paddingVertical: 10,
                borderTopWidth: idx === 0 ? 0 : STROKES.hairline,
                borderTopColor: THEME.border,
                backgroundColor: isSelected ? THEME.accentSoft : "transparent",
              } as ViewStyle,
              Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
            ]}
          >
            <View
              style={{
                width: 14,
                height: 14,
                borderRadius: RADII.full,
                borderWidth: STROKES.hairline,
                borderColor: isSelected ? THEME.accent : THEME.textOnSurfaceFaint,
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {isSelected ? <View style={{ width: 6, height: 6, borderRadius: RADII.full, backgroundColor: THEME.accent }} /> : null}
            </View>
            <Text style={{ fontFamily: THEME.bodySemiBoldFont, fontSize: 12.5, color: isSelected ? THEME.accent : THEME.textOnSurface, minWidth: 96 }}>
              {tier === "unlimited" ? "Unlimited" : `${tier} students`}
            </Text>
            <Text style={{ flex: 1, fontFamily: THEME.bodyFont, fontSize: 11.5, color: isSelected ? THEME.textOnSurfaceDim : THEME.textOnSurfaceFaint }}>
              {row ? tierRateSummary(row) : "Pricing not loaded"}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );
}

function tierRateSummary(row: PricingTier): string {
  const parts: string[] = [];
  if (row.flat_rm_fee_cents != null) parts.push(`${formatCents(row.flat_rm_fee_cents)} flat`);
  parts.push(`${formatCents(row.per_student_rate_cents)}/student`);
  parts.push(`${formatCents(row.per_submission_rate_cents)}/submission`);
  if (row.per_presentation_rate_cents != null) {
    parts.push(`${formatCents(row.per_presentation_rate_cents)}/team presentation`);
  }
  return parts.join(" · ");
}

function MetaRow({ k, v }: { k: string; v: string }) {
  return (
    <View style={{ flexDirection: "row", marginTop: 4, gap: 8 }}>
      <Text style={{ fontFamily: THEME.bodyMediumFont, fontSize: 11, color: THEME.textOnSurfaceDim, minWidth: 140 }}>{k}</Text>
      <Text style={{ flex: 1, fontFamily: THEME.bodyFont, fontSize: 11.5, color: THEME.textOnSurface }}>{v}</Text>
    </View>
  );
}
